/**
 * Global Monaco-style sliding caret.
 *
 * Hides the native blinking caret on every <input>, <textarea>, and
 * contenteditable element, then renders a single fixed-position overlay
 * that smoothly slides between caret positions as the user types or
 * moves the cursor. Uses the medieval theme's accent gold.
 *
 * Opt-out: add the `no-smooth-caret` class to any ancestor (or the
 * input itself). Native caret is preserved for password inputs and
 * IME composition.
 */

import { ACCENT_GOLD } from '../lib/medievalTheme';

const CARET_WIDTH = 3;
const CARET_RADIUS = 2;
const GLIDE_MS = 80;
const BLINK_DELAY_MS = 530;
const GLIDE_TRANSITION = `transform ${GLIDE_MS}ms cubic-bezier(0.2, 0, 0, 1), height ${GLIDE_MS}ms cubic-bezier(0.2, 0, 0, 1), opacity 120ms ease`;

// Computed styles to mirror onto the offscreen measurement div. These
// are the properties that affect text layout. Anything else is noise.
const MIRRORED_STYLES = [
  'direction', 'boxSizing', 'width', 'height',
  'overflowX', 'overflowY',
  'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth', 'borderStyle',
  'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize', 'fontSizeAdjust',
  'lineHeight', 'fontFamily',
  'textAlign', 'textTransform', 'textIndent', 'textDecoration',
  'letterSpacing', 'wordSpacing',
  // Wrap-relevant. Defaults differ between <textarea> and <div>, and
  // copying them is what makes the mirror wrap on the same character
  // boundaries the real textarea does.
  'wordBreak', 'overflowWrap', 'whiteSpace',
  'tabSize',
];

function isTextInput(el) {
  if (!(el instanceof HTMLElement)) return false;
  if (el.closest?.('.no-smooth-caret')) return false;
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName === 'INPUT') {
    const type = (el.getAttribute('type') || 'text').toLowerCase();
    // Only types that show a caret. Skip password. Keep the native
    // caret so the OS/browser handles its (possibly redacted) drawing.
    // Skip number. Chromium hides selectionStart so the overlay can't
    // measure the cursor position; the native caret is shown instead
    // (see injectStyles' input[type="number"] caret-color exception).
    return ['text', 'search', 'url', 'email', 'tel'].includes(type);
  }
  if (el.isContentEditable) return true;
  return false;
}

function isMeasurable(el) {
  if (el.disabled || el.readOnly) return false;
  // Some browsers expose `selectionStart` as null on type="number." Guard.
  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
    return typeof el.selectionStart === 'number';
  }
  return true;
}

/**
 * Measure caret position for an <input> or <textarea> in viewport
 * pixels. Uses the mirror-div technique: an offscreen div is styled
 * identically to the input, filled with text up to the caret position,
 * and a zero-width marker is appended at that point. The marker's
 * bounding rect is the caret position in CSS-pixel space.
 *
 * The input may live under a CSS `zoom`-scaled ancestor (the app uses
 * this on dialog panels). offsetWidth is in unzoomed CSS pixels;
 * getBoundingClientRect is in viewport pixels. Their ratio is the
 * effective zoom, which we multiply the mirror offsets by.
 */
function measureInputCaret(el) {
  const pos = el.selectionStart;
  if (pos == null) return null;

  const computed = window.getComputedStyle(el);
  const isTextarea = el.tagName === 'TEXTAREA';

  const mirror = document.createElement('div');
  const ms = mirror.style;
  ms.position = 'absolute';
  ms.visibility = 'hidden';
  ms.top = '0';
  ms.left = '-99999px';
  for (const prop of MIRRORED_STYLES) ms[prop] = computed[prop];
  // Force pre-wrap on textareas in case the source element resolved
  // white-space to something pre-wrap-incompatible (rare, but cheap
  // to be defensive). Inputs always render as single-line `pre`.
  if (isTextarea) ms.whiteSpace = 'pre-wrap';
  else { ms.whiteSpace = 'pre'; ms.wordWrap = 'normal'; }

  const value = el.value ?? '';
  const before = value.substring(0, pos);
  const after = value.substring(pos);

  // Single-line inputs collapse whitespace; preserve it so the caret
  // measurement matches the rendered text width exactly.
  const beforeText = isTextarea ? before : before.replace(/\s/g, '\u00a0');
  const afterText = isTextarea ? after : after.replace(/\s/g, '\u00a0');

  // The mirror holds the FULL value split around a zero-width marker:
  //   [text before caret][marker][text after caret]
  //
  // Two properties matter:
  //   1. Full text means wrap math sees what the real textarea sees, so
  //      the caret lands on the correct visual line in the middle of
  //      wrapped paragraphs.
  //   2. The marker uses U+2060 WORD JOINER. Zero-width AND explicitly
  //      a no-break opportunity. Earlier marker designs that contained
  //      the trailing text could themselves wrap, dragging the caret
  //      onto the next line BEFORE the user typed the character that
  //      actually causes the wrap; a placeholder '.' had the same flaw
  //      because '.' has width and could push the caret past the line
  //      end. Word Joiner has neither failure mode and still reports a
  //      measurable offsetLeft/offsetTop at its DOM position.
  mirror.appendChild(document.createTextNode(beforeText));
  const marker = document.createElement('span');
  marker.textContent = '\u2060';
  mirror.appendChild(marker);
  mirror.appendChild(document.createTextNode(afterText));

  document.body.appendChild(mirror);
  const markerOffsetTop = marker.offsetTop;
  const localLeft = marker.offsetLeft + parseFloat(computed.borderLeftWidth);
  const fontSize = parseFloat(computed.fontSize);
  const lineHeight = parseFloat(computed.lineHeight) || fontSize * 1.2;
  document.body.removeChild(mirror);

  // Caret height tracks the font, not the line slot. The natural
  // browser behavior (caret = lineHeight tall) produces a caret that
  // visually overshoots the text whenever the input has loose
  // line-height. Which is most styled inputs. Sizing to ~font-size
  // matches what the user perceives as "the text height" and matches
  // VSCode/Monaco. The 1.15 factor adds enough room for ascenders and
  // descenders without padding.
  const caretHeight = fontSize * 1.15;

  // Vertical position: center the caret inside the line slot. For
  // <textarea> the slot's top is the marker's offsetTop + borderTop;
  // for <input> single-line, Chromium auto-centers the line in the
  // content box, so we compute the slot top from the content area.
  let lineSlotTop;
  if (isTextarea) {
    lineSlotTop = markerOffsetTop + parseFloat(computed.borderTopWidth);
  } else {
    const borderTop = parseFloat(computed.borderTopWidth);
    const paddingTop = parseFloat(computed.paddingTop);
    const paddingBottom = parseFloat(computed.paddingBottom);
    const contentHeight = el.clientHeight - paddingTop - paddingBottom;
    lineSlotTop = borderTop + paddingTop + (contentHeight - lineHeight) / 2;
  }
  const localTop = lineSlotTop + (lineHeight - caretHeight) / 2;

  const rect = el.getBoundingClientRect();
  const zoomX = el.offsetWidth ? rect.width / el.offsetWidth : 1;
  const zoomY = el.offsetHeight ? rect.height / el.offsetHeight : 1;

  const x = rect.left + (localLeft - el.scrollLeft) * zoomX;
  const y = rect.top + (localTop - el.scrollTop) * zoomY;
  const h = caretHeight * zoomY;

  // Off-canvas (scrolled out). Hide rather than draw at the edge.
  if (x < rect.left - 1 || x > rect.right + 1) return null;
  if (y + h < rect.top - 1 || y > rect.bottom + 1) return null;

  return { x, y, h };
}

function measureContentEditableCaret(el) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null;
  // The selection must live inside the focused editable. After a
  // programmatic DOM rewrite it can sit on the root or a detached node;
  // drawing there paints a caret outside the box.
  if (!el.contains(sel.anchorNode)) return null;
  const range = sel.getRangeAt(0);
  let rect = range.getBoundingClientRect();
  // Empty line / start of element. GetBoundingClientRect returns 0×0.
  // Insert a zero-width marker, measure it, remove it.
  if (rect.width === 0 && rect.height === 0) {
    const marker = document.createElement('span');
    marker.textContent = '\u200b';
    range.insertNode(marker);
    rect = marker.getBoundingClientRect();
    const parent = marker.parentNode;
    parent?.removeChild(marker);
    parent?.normalize?.();
    // Reselect. DOM mutation invalidated the original range.
    sel.removeAllRanges();
    const restored = document.createRange();
    restored.setStart(range.startContainer, range.startOffset);
    restored.collapse(true);
    sel.addRange(restored);
  }
  if (!rect.height) return null;
  // Same clip as inputs: never paint past the editable's own box.
  const box = el.getBoundingClientRect();
  if (rect.left < box.left - 1 || rect.left > box.right + 1) return null;
  if (rect.bottom < box.top - 1 || rect.top > box.bottom + 1) return null;
  return { x: rect.left, y: rect.top, h: rect.height };
}

function createCaretElement() {
  const el = document.createElement('div');
  el.setAttribute('aria-hidden', 'true');
  el.id = 'smooth-caret-overlay';
  Object.assign(el.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: `${CARET_WIDTH}px`,
    height: '0px',
    background: ACCENT_GOLD,
    borderRadius: `${CARET_RADIUS}px`,
    boxShadow: `0 0 6px ${ACCENT_GOLD}55`,
    pointerEvents: 'none',
    zIndex: '2147483647',
    opacity: '0',
    transform: 'translate3d(0, 0, 0)',
    transition: GLIDE_TRANSITION,
    willChange: 'transform, height, opacity',
  });
  return el;
}

function injectStyles() {
  const style = document.createElement('style');
  style.id = 'smooth-caret-styles';
  style.textContent = `
    /* Hide native caret app-wide. The smooth overlay draws its own. */
    input, textarea, [contenteditable], [contenteditable=""], [contenteditable="true"] {
      caret-color: transparent;
    }
    /* Keep the native caret where the smooth overlay can't reach:
       passwords (privacy), number inputs (Chromium hides selectionStart
       so the overlay can't track the cursor position), and any opt-out
       subtree. */
    input[type="password"],
    input[type="number"],
    .no-smooth-caret, .no-smooth-caret input, .no-smooth-caret textarea,
    .no-smooth-caret [contenteditable] {
      caret-color: auto;
    }
    @keyframes smooth-caret-blink {
      0%, 45%   { opacity: 1; }
      55%, 100% { opacity: 0.15; }
    }
  `;
  document.head.appendChild(style);
}

export function installSmoothCaret() {
  if (document.getElementById('smooth-caret-overlay')) return;

  injectStyles();
  const caret = createCaretElement();
  document.body.appendChild(caret);

  let target = null;
  let composing = false;
  let blinkTimer = 0;
  let rafId = 0;
  let resizeObserver = null;
  let teleportNext = false;

  const hide = () => {
    // Hide instantly (no opacity fade, no transform glide). The caret
    // must not have any visual continuity into the next focus change.
    // otherwise it appears to "fly" from the previous input to the new
    // one. We disable transitions, snap state, then restore the
    // transition for normal in-input movement next time it's shown.
    caret.style.transition = 'none';
    caret.style.opacity = '0';
    caret.style.animation = 'none';
    if (blinkTimer) {
      clearTimeout(blinkTimer);
      blinkTimer = 0;
    }
    void caret.offsetWidth;
    caret.style.transition = GLIDE_TRANSITION;
  };

  const startBlink = () => {
    if (blinkTimer) clearTimeout(blinkTimer);
    caret.style.animation = 'none';
    // Force reflow so the next animation assignment restarts cleanly.
    void caret.offsetWidth;
    blinkTimer = window.setTimeout(() => {
      caret.style.animation = 'smooth-caret-blink 1.06s ease-in-out infinite';
    }, BLINK_DELAY_MS);
  };

  const measure = (el) => {
    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
      // Selection range. Let the native selection rendering own the screen.
      if (el.selectionStart !== el.selectionEnd) return null;
      return measureInputCaret(el);
    }
    if (el.isContentEditable) return measureContentEditableCaret(el);
    return null;
  };

  const update = () => {
    rafId = 0;
    if (!target || composing || !document.hasFocus()) return hide();
    if (!isTextInput(target) || !isMeasurable(target)) return hide();

    const pos = measure(target);
    if (!pos) return hide();

    if (teleportNext) {
      teleportNext = false;
      // First paint after a focus change: snap into position with no
      // glide so the caret doesn't appear to fly from the previous
      // input. Reflow between the two writes commits the transition-
      // less state before we restore the glide for subsequent moves.
      caret.style.transition = 'none';
      caret.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0)`;
      caret.style.height = `${pos.h}px`;
      caret.style.opacity = '1';
      void caret.offsetWidth;
      caret.style.transition = GLIDE_TRANSITION;
    } else {
      caret.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0)`;
      caret.style.height = `${pos.h}px`;
      caret.style.opacity = '1';
    }
  };

  const schedule = () => {
    if (rafId) return;
    rafId = requestAnimationFrame(update);
  };

  // Re-measure + restart blink. Use after user input (caret should
  // remain solid for ~530ms before fading), as opposed to passive
  // updates (scroll/resize) which keep the existing blink phase.
  const bump = () => {
    schedule();
    startBlink();
  };

  document.addEventListener('focusin', (e) => {
    const el = e.target;
    if (!(el instanceof HTMLElement) || !isTextInput(el)) {
      target = null;
      hide();
      return;
    }
    target = el;
    resizeObserver?.disconnect();
    resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(el);
    teleportNext = true;
    bump();
  });

  document.addEventListener('focusout', () => {
    target = null;
    resizeObserver?.disconnect();
    resizeObserver = null;
    hide();
  });

  document.addEventListener('input', (e) => {
    if (e.target === target) bump();
  });

  document.addEventListener('keydown', (e) => {
    if (e.target !== target) return;
    // Arrow keys, Home, End, etc. don't fire `input` but move the caret.
    if (e.key.length > 1 || e.metaKey || e.ctrlKey) bump();
  });

  document.addEventListener('keyup', (e) => {
    if (e.target === target) bump();
  });

  document.addEventListener('mouseup', schedule, true);
  document.addEventListener('selectionchange', schedule);

  document.addEventListener('compositionstart', (e) => {
    if (e.target !== target) return;
    composing = true;
    // Restore native caret during IME composition. It shows the
    // composition underline with its own conventions.
    target.style.caretColor = '';
    hide();
  });
  document.addEventListener('compositionend', (e) => {
    if (e.target !== target) return;
    composing = false;
    target.style.caretColor = 'transparent';
    bump();
  });

  window.addEventListener('scroll', schedule, { capture: true, passive: true });
  window.addEventListener('resize', schedule);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) hide(); else schedule();
  });
  window.addEventListener('blur', hide);
  window.addEventListener('focus', schedule);
}
